import { useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/DropdownMenu";
import FilterTrigger from "./FilterTrigger";
import { warehouseLabel, type WarehouseColumn } from "./StockMatrixHeader";

interface Props {
  warehouses: WarehouseColumn[];
  // external_ids of warehouses whose columns are currently hidden.
  hiddenIds: Set<string>;
  onChange: (next: Set<string>) => void;
}

interface ChannelEntry {
  name: string | null;
  warehouses: WarehouseColumn[];
}

// "Warehouse: All" dropdown: check/uncheck individual warehouses or a whole
// channel to toggle their columns in the stock matrix. Selection lives in the
// page (session-only) — the server always sends every column.
export default function WarehouseFilter({ warehouses, hiddenIds, onChange }: Props) {
  // Same consecutive-run grouping as the matrix header, so the dropdown
  // mirrors the column order on screen.
  const channels = useMemo(() => {
    const out: ChannelEntry[] = [];
    for (const w of warehouses) {
      const last = out[out.length - 1];
      if (last && last.name === w.channel_name) last.warehouses.push(w);
      else out.push({ name: w.channel_name, warehouses: [w] });
    }
    return out;
  }, [warehouses]);

  const toggleWarehouse = (externalId: string) => {
    const next = new Set(hiddenIds);
    if (next.has(externalId)) next.delete(externalId);
    else next.add(externalId);
    onChange(next);
  };

  const toggleChannel = (entry: ChannelEntry) => {
    const next = new Set(hiddenIds);
    const allVisible = entry.warehouses.every((w) => !next.has(w.external_id));
    for (const w of entry.warehouses) {
      if (allVisible) next.add(w.external_id);
      else next.delete(w.external_id);
    }
    onChange(next);
  };

  const channelState = (entry: ChannelEntry): boolean | "indeterminate" => {
    const visible = entry.warehouses.filter((w) => !hiddenIds.has(w.external_id)).length;
    if (visible === 0) return false;
    if (visible === entry.warehouses.length) return true;
    return "indeterminate";
  };

  const visibleCount = warehouses.length - hiddenIds.size;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterTrigger
          label="Warehouse"
          value={hiddenIds.size > 0 ? `${visibleCount} of ${warehouses.length}` : "All"}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-96 overflow-y-auto">
        {channels.map((entry, idx) => (
          <div key={`${entry.name}-${idx}`}>
            {idx > 0 && <DropdownMenuSeparator />}
            {entry.name ? (
              <DropdownMenuCheckboxItem
                checked={channelState(entry)}
                onCheckedChange={() => toggleChannel(entry)}
                onSelect={(e) => e.preventDefault()}
                className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {entry.name}
              </DropdownMenuCheckboxItem>
            ) : (
              <DropdownMenuLabel>Unassigned</DropdownMenuLabel>
            )}
            {entry.warehouses.map((w) => (
              <DropdownMenuCheckboxItem
                key={w.external_id}
                checked={!hiddenIds.has(w.external_id)}
                onCheckedChange={() => toggleWarehouse(w.external_id)}
                onSelect={(e) => e.preventDefault()}
              >
                {warehouseLabel(w)}
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
